import { supabase } from './supabaseClient';
import { compressImage } from './imageUpload';

/**
 * Upload multiple media files (images and videos) to Supabase Storage
 * @param {File[]} files - Array of files to upload
 * @param {string} userId - User ID for organizing uploads
 * @returns {Promise<{success: boolean, urls?: string[], errors?: string[]}>}
 */
export const uploadMultipleMedia = async (files, userId = 'anonymous') => {
  try {
    if (!files || files.length === 0) {
      return { success: false, errors: ['No files provided'] };
    }

    const uploadPromises = files.map(file => uploadSingleMedia(file, userId));
    const results = await Promise.all(uploadPromises);

    const successfulUploads = results.filter(r => r.success);
    const failedUploads = results.filter(r => !r.success);

    if (successfulUploads.length === 0) {
      return { 
        success: false, 
        errors: failedUploads.map(r => r.error) 
      };
    }

    return {
      success: true,
      urls: successfulUploads.map(r => r.url),
      errors: failedUploads.length > 0 ? failedUploads.map(r => r.error) : undefined
    };
  } catch (error) {
    console.error('Multiple media upload error:', error);
    return { success: false, errors: [error.message] };
  }
};

/**
 * Upload a single media file (image or video) to Supabase Storage
 * @param {File} file - The file to upload
 * @param {string} userId - User ID for organizing uploads
 * @returns {Promise<{success: boolean, url?: string, error?: string, type?: string}>}
 */
const uploadSingleMedia = async (file, userId = 'anonymous') => {
  try {
    if (!file) {
      return { success: false, error: 'No file provided' };
    }

    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (!isImage && !isVideo) {
      return { success: false, error: `${file.name}: Must be an image or video` };
    }

    // Block HEIC files
    if (file.type === 'image/heic' || file.type === 'image/heif' || 
        file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif')) {
      return { 
        success: false, 
        error: `${file.name}: HEIC format not supported. Please use JPG or PNG.` 
      };
    }

    // Check file size (max 200MB for videos, 10MB for images)
    const maxSize = isVideo ? 200 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / (1024 * 1024);
      return { 
        success: false, 
        error: `${file.name}: File too large (max ${maxSizeMB}MB)` 
      };
    }

    const timestamp = Date.now();
    const randomId = Math.random().toString(36).substring(7);
    let uploadFile;
    let fileName;

    if (isImage) {
      // Compress images
      try {
        const compressedBlob = await compressImage(file, 1200, 1200, 0.8);
        fileName = `${userId}/${timestamp}-${randomId}.jpg`;
        uploadFile = new File([compressedBlob], fileName, {
          type: 'image/jpeg',
          lastModified: Date.now()
        });
      } catch (compressionError) {
        console.warn('Compression failed, using original:', compressionError.message);
        const fileExt = file.name.split('.').pop() || 'jpg';
        fileName = `${userId}/${timestamp}-${randomId}.${fileExt}`;
        uploadFile = new File([file], fileName, {
          type: file.type,
          lastModified: Date.now()
        });
      }
    } else {
      // Videos - upload as-is
      const fileExt = file.name.split('.').pop() || 'mp4';
      fileName = `${userId}/${timestamp}-${randomId}.${fileExt}`;
      uploadFile = new File([file], fileName, {
        type: file.type,
        lastModified: Date.now()
      });
    }

    // Upload to forum-images bucket (handles both images and videos)
    const { data, error } = await supabase.storage
      .from('forum-images')
      .upload(fileName, uploadFile, {
        cacheControl: '31536000',
        upsert: false
      });

    if (error) {
      console.error('Media upload error:', error);
      return { success: false, error: `${file.name}: ${error.message}` };
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('forum-images')
      .getPublicUrl(fileName);

    return { 
      success: true, 
      url: urlData?.publicUrl,
      type: isVideo ? 'video' : 'image'
    };
  } catch (error) {
    console.error('Single media upload error:', error);
    return { success: false, error: `${file.name}: ${error.message}` };
  }
};

/**
 * Delete multiple media files from Supabase Storage
 * @param {string[]} urls - Array of URLs to delete
 * @returns {Promise<{success: boolean, errors?: string[]}>}
 */
export const deleteMultipleMedia = async (urls) => {
  try {
    if (!urls || urls.length === 0) {
      return { success: true };
    }

    const deletePromises = urls.map(url => deleteSingleMedia(url));
    const results = await Promise.all(deletePromises);

    const failedDeletes = results.filter(r => !r.success);

    if (failedDeletes.length > 0) {
      return {
        success: false,
        errors: failedDeletes.map(r => r.error)
      };
    }

    return { success: true };
  } catch (error) {
    console.error('Multiple media delete error:', error);
    return { success: false, errors: [error.message] };
  }
};

/**
 * Delete a single media file from Supabase Storage
 * @param {string} url - The URL to delete
 * @returns {Promise<{success: boolean, error?: string}>}
 */
const deleteSingleMedia = async (url) => {
  try {
    if (!url) {
      return { success: true };
    }

    let filePath = url;
    if (url.includes('forum-images/')) {
      const urlParts = url.split('/forum-images/');
      filePath = urlParts[urlParts.length - 1];
    }

    const { error } = await supabase.storage
      .from('forum-images')
      .remove([filePath]);

    if (error) {
      console.error('Delete media error:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Delete media error:', error);
    return { success: false, error: error.message };
  }
};
